/**
 * Route copy for `src/routes/planning` (keys: `routes.planning.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  calendarPreviewTable: {
    noPostProposed: 'No post proposed',
    adjustTheCadenceTheThemesOr: 'Adjust the cadence, the themes or the date range, then rerun the preview.',
    post: 'post',
    proposed: 'proposed',
    timeZoneTimezone: '· time zone {{timezone}}',
    previewNothingIsSaved: 'Preview — nothing is saved',
    planSaved: 'Plan saved',
    conflictcountConflictS: '⛔ {{conflictCount}} conflict(s)',
    partialSignalsTheCadenceWasEstimated: '⚠ Partial signals — the cadence was estimated heuristically.',
    when: 'When',
    platform: 'Platform',
    derivation: 'Derivation',
    status: 'Status',
    score: 'Score',
    generation: 'generation',
  },
  chips: {
    draft: 'Draft',
    generating: 'Generating…',
    awaitingApproval: 'Awaiting approval',
    scheduled: 'Scheduled',
    blocked: 'Blocked',
  },
  composerExtras: {
    editedComparedWithTheGeneratedText: '✎ Edited compared with the generated text (',
    characterSVOnSaveThe: 'character(s), ~{{v}}%). On save, the AI will learn from your edits.',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    changesTakenIntoAccountForLearning: '✓ Changes taken into account for learning',
    savedLearningNotCaptured: 'Saved — learning not captured.',
    noSignificantChange: 'No significant change.',
  },
  contentEnginePolicyPanel: {
    disabled: 'Disabled',
    couldNotLoadTheAutomations: 'Could not load the automations.',
    automationsUpdated: 'Automations updated.',
    updateRefused: 'Update refused.',
    eraseMyLearningData: 'Erase my learning data?',
    thisPermanentlyDeletesTheContentMemories: 'This permanently deletes the content memories, model statistics and review histories derived from your content. Irreversible.',
    erase: 'Erase',
    learningDataErased: 'Learning data erased.',
    couldNotErase: 'Could not erase.',
    loadingAutomations: 'Loading automations…',
    readOnlyPreviewTheAutomationService: 'Read-only preview — the automation service is not deployed on this environment yet. Everything is shown in the disabled state (default).',
    readOnlyOnlyAnOwnerAn: 'Read-only — only an owner, an administrator or a manager can change the content engine\'s automations.',
    law25NoticeAutomatedDecisions: 'Law 25 notice — automated decisions',
    byDefaultTheAi: 'By default, the AI',
    suggestsAndDrafts: 'suggests and drafts',
    nothingIsPublishedAnsweredOrApplied: ': nothing is published, answered or applied automatically. Turning on an',
    automatic: 'automatic',
    modeMeansADecisionMayBe: 'mode means a decision may be taken without human intervention, under the configured confidence threshold and daily cap, with audit logging and the ability to contest it. You can turn these automations off at any time.',
    iHaveReadAndUnderstandThis: 'I have read and understand this notice on automated decisions (required before turning on an automatic mode).',
    memoryLearning: 'Memory learning',
    theAiLearnsFromYourApprovals: 'The AI learns from your approvals, rejections, revisions and edits to improve the next generations.',
    noLearningDataIsCollectedEvery: 'No learning data is collected; every generation starts fresh.',
    multiExpertReviewLoop: 'Multi-expert review loop',
    aPanelOfAiExpertsAssesses: 'A panel of AI experts assesses a draft, then has it revised automatically before suggesting it.',
    reviewStaysManualYouStartIt: 'Review stays manual: you start it yourself from the composer.',
    mode: 'Mode:',
    acceptTheLaw25NoticeTo: 'Accept the Law 25 notice to turn on automatic mode.',
    suggest: 'Suggest',
    draft: 'Draft',
    automatic2: 'Automatic',
    maxRounds: 'Max rounds:',
    learnedModelSelection: 'Learned model selection',
    theAiFavorsTheBestPerforming: 'The AI favors the best-performing models (epsilon-greedy) instead of a simple rotation.',
    selectionStaysDeterministicRotationPerDa: 'Selection stays deterministic (rotation / per day / per theme) — no learning.',
    durableDeferredGeneration: 'Durable deferred generation',
    generatesTheContent24HBefore: 'Generates the content ~24 h before publishing (durable queue, retried on restart).',
    contentIsOnlyGeneratedWhenYou: 'Content is only generated when you ask for it (immediate draft).',
    automaticCalendarGeneration: 'Automatic calendar generation',
    letsTheEngineProposeAndSchedule: 'Lets the engine propose and schedule content calendars on a cadence.',
    youGenerateEachCalendarManuallyFrom: 'You generate each calendar manually from the assistant (preview before saving).',
    acceptTheLaw25NoticeTo2: 'Accept the Law 25 notice to turn on this automation.',
    confidenceThresholdAuto: 'Confidence threshold (auto)',
    dailyCapAuto: 'Daily cap (auto)',
    myLearningData: 'My learning data',
    permanentlyDeletesWhatTheAiLearned: 'Permanently deletes what the AI learned from your content (memories, model statistics, review histories).',
    erasing: 'Erasing…',
    eraseMyLearningData2: 'Erase my learning data',
    contentEngineAutomations: 'Content engine — automations',
    everythingIsOffByDefaultThe: 'Everything is off by default. The AI suggests and drafts; you stay in control.',
  },
  derivationBadge: {
    model: 'Model',
    theme: 'Theme',
    slot: 'Slot',
    review: 'Review',
  },
  expertLoopControls: {
    autoReviewFailedTryAgain: 'Auto-review failed — try again.',
    autoReviseWithTheExperts: '🔁 Auto-revise with the experts',
    disabled: 'Disabled',
    aPanelOfAiExpertsAssesses: 'A panel of AI experts assesses the text and has it revised until it is ready. Nothing is applied without your approval.',
    maxRounds: 'Max rounds:',
    looping: 'Looping…',
    start: '▶ Start',
    firstWriteTheTextToRevise: 'First write the text to revise.',
    ready: '✓ Ready',
    needsWork: '↻ Needs work',
    lengthRoundS: '{{length}} round(s)',
    applyTheRevisedDraft: 'Apply the revised draft',
    theBestRoundMatchesYourCurrent: 'The best round matches your current text — nothing to apply.',
  },
  generateCalendarWizard: {
    mode: 'Mode',
    parameters: 'Parameters',
    modelReview: 'Model & review',
    preview: 'Preview',
    structured: 'Structured',
    youSetTheExactDaysAnd: 'You set the exact days and slots.',
    automatic: 'Automatic',
    theCadenceAndSlotsAreDerived: 'The cadence and slots are derived from your analytics (best times, trends).',
    orientation: 'Orientation',
    describeYourIntentAnAiStrategist: 'Describe your intent: an AI strategist derives the calendar structure from it.',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    scheduleTheSlotsOnlyContentLater: 'Schedule the slots only (content later).',
    draftNow: 'Draft now',
    generateTheContentAsADraft: 'Generate the content as a draft right away.',
    deferredT24H: 'Deferred (T-24 h)',
    generate24HBeforeEachPost: 'Generate ~24 h before each post.',
    couldNotGenerateThePreview: 'Could not generate the preview.',
    planSavedVPostSAs: 'Plan saved — {{v}} post(s) as drafts.',
    couldNotSaveThePlan: 'Could not save the plan.',
    generateAContentCalendar: 'Generate a content calendar',
    threeModesAPreviewBeforeSaving: 'Three modes, a preview before saving. Everything is saved as a draft — nothing is published automatically.',
    calendar: '← Calendar',
    steps: 'Steps',
    themes: 'Themes',
    onePerLineOrCommaSeparated: 'One per line, or comma-separated.',
    agenticArchitectureLaw25ComplianceTeam: 'Agentic architecture\nLaw 25 compliance\nTeam culture',
    platforms: 'Platforms',
    publishingDays: 'Publishing days',
    timeSlotsLocal: 'Time slots (local)',
    remove: 'Remove',
    addASlot: '+ Add a slot',
    frequencyPostsWeek: 'Frequency (posts / week)',
    theExactCadenceWillBeRefined: 'The exact cadence will be refined by your analytics.',
    strategicBrief: 'Strategic brief',
    describeTheGoalAudienceToneAnd: 'Describe the goal, audience, tone and intent.',
    eGPositionKarexiaAsThe: 'E.g. Position Karexia as the go-to agentic AI reference in Quebec; audience: SMB leaders; educational tone; 1 case study / week.',
    numberOfPosts: 'Number of posts',
    max90: 'max 90',
    start: 'Start',
    end: 'End',
    timeZone: 'Time zone',
    modelChoice: 'Model choice',
    multiExpertReviewDuringGeneration: '🔁 Multi-expert review during generation',
    disabled: 'Disabled',
    everyGeneratedPostGoesThroughThe: 'Every generated post goes through the generate → review → revise loop. It can only be set lower than the content engine policy.',
    maxRounds: 'Max rounds',
    experts: 'Experts',
    contentGeneration: 'Content generation',
    previewGeneratedIn: 'Preview generated in',
    dryRun: 'dry-run',
    modeNothingIsSavedUntilYou: 'mode — nothing is saved until you click “Save the plan”.',
    generating: 'Generating…',
    regenerateThePreview: '↻ Regenerate the preview',
    generatingThePreview: 'Generating the preview…',
    runAGenerationToSeeThe: 'Run a generation to see the preview.',
    previous: '← Previous',
    next: 'Next →',
    generateThePreview: 'Generate the preview →',
    saving: 'Saving…',
    saveThePlanDraft: '✓ Save the plan (draft)',
    none: 'None',
  },
  postReviewTimeline: {
    inProgress: 'In progress',
    done: 'Done',
    roundCapReached: 'Round cap reached',
    readyToPublish: 'Ready to publish',
    scorePlateauReached: 'Score plateau reached',
    humanReviewRequired: 'Human review required',
    degraded: 'Degraded',
    failed: 'Failed',
    roundRound: 'Round {{round}}',
    publish: '✓ Publish',
    revise: '↻ Revise',
    bestRoundKept: '★ Best round (kept)',
    degradedEvaluators: '⚠ Degraded evaluators',
    critiquePassedToTheWriterFor: 'Critique passed to the writer for the next round:',
    hideTheDraft: 'Hide the draft',
    viewThisRoundSDraft: 'View this round\'s draft',
    reviewLoopFailedTryAgain: 'Review loop failed — try again.',
    agenticLoopGenerateReviewRevise: '🔁 Agentic loop generate → review → revise',
    experts: 'Experts',
    maxRounds: 'Max rounds',
    loopRunning: 'Loop running…',
    startTheLoop: '▶ Start the loop',
    writeTheContentToBeAssessed: 'Write the content to be assessed to start the loop.',
    finalVerdict: 'Final verdict:',
    readyToPublish2: '✓ Ready to publish',
    needsWork: '↻ Needs work',
    lengthRoundS: '· {{length}} round(s)',
    modelModel: 'Model: {{model}}',
    manualReviewEditablePanel: 'Manual review (editable panel)',
  },
  reviewWorkshopPage: {
    aiReviewWorkshop: 'AI review workshop',
    haveADraftAssessedAndRevised: 'Have a draft assessed and revised by a panel of experts, then keep the best round.',
    theme: 'Theme',
    derivesTheExpertPanelFromThe: 'Derives the expert panel from the topic.',
    eGAgenticArchitectureLaw25: 'e.g. Agentic architecture, Law 25 compliance…',
    platform: 'Platform',
    draftToAssess: 'Draft to assess',
    pasteOrWriteTheContentFor: 'Paste or write the content for the panel to review…',
    saveMyEditsLearning: 'Save my edits (learning)',
  },
  templateLeaderboard: {
    reviewScore: 'Review score',
    engagement: 'Engagement',
    liftVsMedian: 'Lift vs median',
    uses: 'Uses',
    statisticsUnavailable: 'Statistics unavailable.',
    loadingStatistics: 'Loading statistics…',
    published: 'Published',
    averageScore: 'Average score',
    lift: 'Lift',
    couldNotLoadTheTemplateRanking: 'Could not load the template ranking.',
    platform: 'Platform',
    sortBy: 'Sort by',
    loadingTheRanking: 'Loading the ranking…',
    rankingNotAvailable: 'Ranking not available',
    theTemplateLibraryAndItsPerformance: 'The template library and its performance tracking are not deployed on this environment yet.',
    noDataYet: 'No data yet',
    publishAFewPostsFromTemplates: 'Publish a few posts from templates to see their performance appear here.',
    viral: 'viral',
    usesUses: '{{uses}} uses',
    linkedinTemplatesRanking: 'LinkedIn templates — ranking',
    theTemplatesThatPerformBestFor: 'The templates that perform best for your account, and their statistics.',
  },
  templateModePicker: {
    specificTemplate: 'Specific template',
    alwaysTheSameTemplateYouChoose: 'Always the same template you choose.',
    byDay: 'By day',
    theTemplateTiedToThePublishing: 'The template tied to the publishing day.',
    byTheme: 'By theme',
    theTemplateBestSuitedToThe: 'The template best suited to the post\'s theme.',
    random: 'Random',
    aRandomTemplateVariesTheStyle: 'A random template (varies the style).',
    rotation: 'Rotation',
    rotatesTheTemplatesDeterministically: 'Rotates the templates deterministically.',
    learnedAi: 'Learned (AI)',
    theAiFavorsTheBestPerforming: 'The AI favors the best-performing templates. Falls back to “Rotation” if learning is turned off.',
    previewUnavailableSelectorNotDeployed: 'Preview unavailable (selector not deployed).',
    couldNotPreviewTheTemplate: 'Could not preview the template.',
    ai: 'AI',
    model: 'Model',
    chooseATemplate: '— choose a template —',
    library: ' (library)',
    aiAutomationOffByDefaultLearned: 'AI automation — off by default. “Learned” selection only uses performance if learning is enabled in the content engine automations; otherwise it behaves like “Rotation”.',
    previewing: 'Previewing…',
    previewTheSelection: '👁 Preview the selection',
    noTemplateSelectedReason: 'No template selected — {{reason}}',
    fallingBackToRotation: '(falling back to “Rotation”)',
  },
};

export default en;
